import { usdFormat } from "../../../../utils";
import { ItemReportRow } from "./type";
import { ColDef, CellStyle } from "ag-grid-community";

type DisplayType = 'by_item' | 'by_day_by_item' | 'by_location_by_item' | 'by_day_by_location_by_item' | 'by_hour_by_item_pivot';

// A ticketing order with an empty items array yields a report row with a null pos_name.
// Normalize it once so grouping keys, sort comparators and chart labels never see null.
const NO_ITEM_NAME = '(no item)';

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  };
  return date.toLocaleDateString('en-US', options);
};

const generateColDefs = (
  displayType: DisplayType = 'by_item',
  _expandedRows?: Set<string>,
  _toggleExpansion?: (rowId: string) => void
): ColDef<ItemReportRow>[] => {
  const cols: ColDef<ItemReportRow>[] = [];

  if (displayType === 'by_day_by_item' || displayType === 'by_day_by_location_by_item') {
    cols.push({
      headerName: 'Date',
      field: 'transaction_date',
      pinned: 'left',
      filter: 'agTextColumnFilter',
      valueFormatter: params => formatDate(params.value),
      cellStyle: params => {
        if (params.data?.isGroupRow) {
          return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
        }
        if (params.data?.pos_name === 'Total') {
          return { fontWeight: 'bold' } as CellStyle;
        }
        return undefined;
      }
    });
  }

  if (displayType === 'by_location_by_item' || displayType === 'by_day_by_location_by_item') {
    cols.push({
      headerName: 'Location',
      field: 'location_name',
      pinned: 'left',
      filter: 'agTextColumnFilter',
      cellStyle: params => {
        if (params.data?.isGroupRow) {
          return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
        }
        if (params.data?.pos_name === 'Total') {
          return { fontWeight: 'bold' } as CellStyle;
        }
        return undefined;
      }
    });
  }

  cols.push({
    headerName: 'Ticketing Item Name',
    field: 'pos_name',
    pinned: 'left',
    filter: 'agTextColumnFilter',
    cellStyle: params => {
      const level = params.data?.groupLevel;

      if (params.value === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }

      if (params.data?.isGroupRow) {
        if (level === 1) {
          return {
            fontWeight: 'bold',
            backgroundColor: '#e5e7eb',
            fontSize: '15px',
            color: '#1f2937'
          } as CellStyle;
        }
        if (level === 2) {
          return {
            fontWeight: 'bold',
            backgroundColor: '#f3f4f6',
            paddingLeft: '20px',
            color: '#374151'
          } as CellStyle;
        }
      }

      if (!params.data?.isGroupRow && level === 0) {
        return { paddingLeft: '20px' } as CellStyle;
      }

      return undefined;
    }
  });

  cols.push({
    headerName: 'Total - # of units sold',
    field: 'total_units_sold',
    width: 170,
    valueFormatter: params => params.value ?? 0,
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Total Gross Sales',
    field: 'total_gross_sales',
    width: 140,
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Total Tax',
    field: 'total_tax',
    width: 130,
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Price',
    field: 'item_price',
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Total Net Sales',
    field: 'total_net_sales',
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Item Category',
    field: 'product_type',
    filter: 'agTextColumnFilter',
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Item Description',
    field: 'description',
    filter: 'agTextColumnFilter',
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Total Discount',
    field: 'total_discounts',
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  cols.push({
    headerName: 'Total Promo Codes',
    field: 'total_promo_discounts',
    valueFormatter: params => usdFormat.format(params.value ?? 0),
    cellStyle: params => {
      if (params.data?.isGroupRow) {
        return { fontWeight: 'bold', backgroundColor: '#f3f4f6' } as CellStyle;
      }
      if (params.data?.pos_name === 'Total') {
        return { fontWeight: 'bold' } as CellStyle;
      }
      return undefined;
    }
  });

  return cols;
};

export const colDefs: ColDef<ItemReportRow>[] = generateColDefs();

export const getGroupedColDefs = (
  displayType: string,
  expandedRows?: Set<string>,
  toggleExpansion?: (rowId: string) => void
): ColDef<ItemReportRow>[] => {
  return generateColDefs(displayType as DisplayType, expandedRows, toggleExpansion);
};

export const getAutoGroupColumnDef = (displayType: DisplayType) => {
  return {
    headerName: displayType === 'by_day_by_item' ? 'Date / Item' :
                displayType === 'by_location_by_item' ? 'Location / Item' :
                displayType === 'by_day_by_location_by_item' ? 'Date / Location / Item' : 'Item',
    minWidth: 300,
    pinned: 'left' as const,
    cellRendererParams: {
      suppressCount: true,
    }
  };
};

const formatHourLabel = (hour: number): string => {
  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
};

const formatShortDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
};

export interface PivotRow {
  id: string;
  transaction_date: string;
  hour_label: string;
  hour_sort: number;
  isTotal?: boolean;
  [key: string]: any;
}

export const formatPivotData = (data: ItemReportRow[]): { rows: PivotRow[]; totalRow: PivotRow[]; itemNames: string[] } => {
  if (!data || data.length === 0) return { rows: [], totalRow: [], itemNames: [] };

  const itemNamesSet = new Set<string>();
  const buckets: Record<string, PivotRow> = {};

  data.forEach((curr) => {
    const posName = curr.pos_name ?? NO_ITEM_NAME;
    itemNamesSet.add(posName);

    let hour = 0;
    let dateKey = curr.transaction_date || '';
    if (curr.transaction_hour) {
      const d = new Date(curr.transaction_hour);
      hour = d.getUTCHours();
      dateKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    }

    const bucketKey = `${dateKey}||${hour}`;
    if (!buckets[bucketKey]) {
      buckets[bucketKey] = {
        id: bucketKey,
        transaction_date: dateKey,
        hour_label: formatHourLabel(hour),
        hour_sort: hour,
      };
    }

    const row = buckets[bucketKey];
    const unitsKey = `${posName}_units`;
    const grossKey = `${posName}_gross`;
    const totalGross = (curr.gross_cash_balance_sales + curr.gross_cash_sales + curr.gross_credit_sales + curr.gross_promo_balance_sales + curr.gross_rfid_sales + curr.gross_token_sales) / 100;

    row[unitsKey] = (row[unitsKey] || 0) + curr.total_units_sold;
    row[grossKey] = (row[grossKey] || 0) + totalGross;
  });

  const itemNames = Array.from(itemNamesSet).sort();

  const rows = Object.values(buckets).sort((a, b) => {
    if (a.transaction_date !== b.transaction_date) {
      return b.transaction_date.localeCompare(a.transaction_date);
    }
    return b.hour_sort - a.hour_sort;
  });

  // Show date only on first row of each date group
  let lastDate = '';
  rows.forEach(row => {
    if (row.transaction_date === lastDate) {
      row.transaction_date = '';
    } else {
      lastDate = row.transaction_date;
      row.transaction_date = formatDate(row.transaction_date);
    }
  });

  // Build total row
  const total: PivotRow = {
    id: 'total-row',
    transaction_date: '',
    hour_label: 'Total',
    hour_sort: -1,
    isTotal: true,
  };
  for (const itemName of itemNames) {
    total[`${itemName}_units`] = 0;
    total[`${itemName}_gross`] = 0;
  }
  Object.values(buckets).forEach(row => {
    for (const itemName of itemNames) {
      total[`${itemName}_units`] += row[`${itemName}_units`] || 0;
      total[`${itemName}_gross`] += row[`${itemName}_gross`] || 0;
    }
  });

  return { rows, totalRow: [total], itemNames };
};

export const generatePivotColDefs = (itemNames: string[]): ColDef<any>[] => {
  const cols: ColDef<any>[] = [
    {
      headerName: '',
      field: 'transaction_date',
      pinned: 'left',
      width: 180,
      cellStyle: (params: any) => {
        if (params.data?.isTotal) return { fontWeight: 'bold' };
        return undefined;
      },
    },
    {
      headerName: '',
      field: 'hour_label',
      pinned: 'left',
      width: 100,
      cellStyle: (params: any) => {
        if (params.data?.isTotal) return { fontWeight: 'bold' };
        return undefined;
      },
    },
  ];

  for (const itemName of itemNames) {
    cols.push({
      headerName: `${itemName}`,
      children: [
        {
          headerName: '# of Units',
          field: `${itemName}_units`,
          width: 100,
          valueFormatter: (params: any) => params.value ?? 0,
          cellStyle: (params: any) => {
            if (params.data?.isTotal) return { fontWeight: 'bold' };
            if (!params.value) return { color: '#d1d5db' };
            return undefined;
          },
        },
        {
          headerName: 'Total Gross Sales',
          field: `${itemName}_gross`,
          width: 120,
          valueFormatter: (params: any) => usdFormat.format(params.value ?? 0),
          cellStyle: (params: any) => {
            if (params.data?.isTotal) return { fontWeight: 'bold' };
            if (!params.value) return { color: '#d1d5db' };
            return undefined;
          },
        },
      ] as ColDef<any>[],
    } as ColDef<any>);
  }

  return cols;
};

export const formatData = (data: ItemReportRow[], displayType: string = 'by_item') => {
  if (!data) return [];

  let totalRow: ItemReportRow = {
    id: "total-row",
    pos_name: "Total",
    total_gross_sales: 0,
    total_tax: 0,
    total_discounts: 0,
    total_refunds: 0,
    total_net_sales: 0,
    cash_units_sold: 0,
    credit_units_sold: 0,
    rfid_units_sold: 0,
    cash_balance_units_sold: 0,
    token_units_sold: 0,
    total_units_sold: 0,
    location_id: 0,
    order_id: "",
    transaction_date: "",
    item_price: 0,
    token_price: 0,
    redeemable_token_id: 0,
    product_type: "",
    gross_cash_sales: 0,
    gross_cash_balance_sales: 0,
    gross_credit_sales: 0,
    gross_rfid_sales: 0,
    gross_promo_balance_sales: 0,
    gross_token_sales: 0,
    description: "",
    gl_account_number: "",
    gl_account_name: "",
    total_promo_discounts: 0,
    location_name:'',
  };

  // Process and format data based on currency
  const processedData = data.map((curr) => {
    const item_price = curr.item_price / 100;
    const token_price = curr.token_price;
    const total_gross_sales = (curr.gross_cash_balance_sales + curr.gross_cash_sales + curr.gross_credit_sales + curr.gross_promo_balance_sales + curr.gross_rfid_sales + curr.gross_token_sales) / 100;
    const total_discounts = curr.total_discounts / 100;
    const total_promo_discounts = curr.total_promo_discounts / 100;
    const total_refunds = curr.total_refunds / 100;
    const total_net_sales = ((curr.gross_cash_balance_sales + curr.gross_cash_sales + curr.gross_credit_sales + curr.gross_promo_balance_sales + curr.gross_rfid_sales + curr.gross_token_sales) / 100) -
        ((curr.gross_token_sales + curr.total_refunds + curr.gross_promo_balance_sales + curr.total_discounts + curr.total_promo_discounts + curr.total_tax) / 100);
    const total_tax = curr.total_tax / 100;

    // Update total row
    totalRow.total_gross_sales += total_gross_sales;
    totalRow.total_tax += total_tax;
    totalRow.total_discounts += total_discounts;
    totalRow.total_promo_discounts += total_promo_discounts;
    totalRow.total_refunds += total_refunds;
    totalRow.total_net_sales += total_net_sales;
    totalRow.cash_units_sold += curr.cash_units_sold;
    totalRow.cash_balance_units_sold += curr.cash_balance_units_sold;
    totalRow.credit_units_sold += curr.credit_units_sold;
    totalRow.rfid_units_sold += curr.rfid_units_sold;
    totalRow.token_units_sold += curr.token_units_sold;
    totalRow.total_units_sold += curr.total_units_sold;

    return {
      ...curr,
      pos_name: curr.pos_name ?? NO_ITEM_NAME,
      item_price,
      token_price,
      total_gross_sales,
      total_tax,
      total_discounts,
      total_promo_discounts,
      total_refunds,
      total_net_sales,
    };
  });

  // For 'by_item', just group by item name
  if (displayType === 'by_item') {
    const groupedRows: Record<string, ItemReportRow> = {};

    processedData.forEach((curr) => {
      const key = `${curr.pos_name}||${curr.description}`;

      if (groupedRows[key]) {
        const row = groupedRows[key];
        row.total_gross_sales += curr.total_gross_sales;
        row.total_tax += curr.total_tax;
        row.total_discounts += curr.total_discounts;
        row.total_promo_discounts += curr.total_promo_discounts;
        row.total_refunds += curr.total_refunds;
        row.total_net_sales += curr.total_net_sales;
        row.cash_units_sold += curr.cash_units_sold;
        row.cash_balance_units_sold += curr.cash_balance_units_sold;
        row.credit_units_sold += curr.credit_units_sold;
        row.rfid_units_sold += curr.rfid_units_sold;
        row.token_units_sold += curr.token_units_sold;
        row.total_units_sold += curr.total_units_sold;
      } else {
        groupedRows[key] = { ...curr, id: key };
      }
    });

    groupedRows[totalRow.id] = totalRow;
    return Object.values(groupedRows);
  }

  // For grouped displays, we need to insert group header rows
  const result: ItemReportRow[] = [];

  if (displayType === 'by_day_by_item') {
    const groupedRows: Record<string, ItemReportRow> = {};

    processedData.forEach((curr) => {
      const key = `${curr.transaction_date}||${curr.pos_name}||${curr.description}`;

      if (groupedRows[key]) {
        const row = groupedRows[key];
        row.total_gross_sales += curr.total_gross_sales;
        row.total_tax += curr.total_tax;
        row.total_discounts += curr.total_discounts;
        row.total_promo_discounts += curr.total_promo_discounts;
        row.total_refunds += curr.total_refunds;
        row.total_net_sales += curr.total_net_sales;
        row.cash_units_sold += curr.cash_units_sold;
        row.cash_balance_units_sold += curr.cash_balance_units_sold;
        row.credit_units_sold += curr.credit_units_sold;
        row.rfid_units_sold += curr.rfid_units_sold;
        row.token_units_sold += curr.token_units_sold;
        row.total_units_sold += curr.total_units_sold;
      } else {
        groupedRows[key] = { ...curr, id: key };
      }
    });

    const sortedRows = Object.values(groupedRows).sort((a, b) => {
      const dateA = a.transaction_hour || a.transaction_date;
      const dateB = b.transaction_hour || b.transaction_date;
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA);
      }
      return a.pos_name.localeCompare(b.pos_name);
    });

    result.push(...sortedRows);
  } else if (displayType === 'by_location_by_item') {
    const groupedRows: Record<string, ItemReportRow> = {};

    processedData.forEach((curr) => {
      const locKey = curr.location_name || 'Unknown';
      const key = `${locKey}||${curr.pos_name}||${curr.description}`;

      if (groupedRows[key]) {
        const row = groupedRows[key];
        row.total_gross_sales += curr.total_gross_sales;
        row.total_tax += curr.total_tax;
        row.total_discounts += curr.total_discounts;
        row.total_promo_discounts += curr.total_promo_discounts;
        row.total_refunds += curr.total_refunds;
        row.total_net_sales += curr.total_net_sales;
        row.cash_units_sold += curr.cash_units_sold;
        row.cash_balance_units_sold += curr.cash_balance_units_sold;
        row.credit_units_sold += curr.credit_units_sold;
        row.rfid_units_sold += curr.rfid_units_sold;
        row.token_units_sold += curr.token_units_sold;
        row.total_units_sold += curr.total_units_sold;
      } else {
        groupedRows[key] = { ...curr, location_name: locKey, id: key };
      }
    });

    // Sort by location, then by item name
    const sortedRows = Object.values(groupedRows).sort((a, b) => {
      const locA = a.location_name || 'Unknown';
      const locB = b.location_name || 'Unknown';
      if (locA !== locB) {
        return locA.localeCompare(locB);
      }
      return a.pos_name.localeCompare(b.pos_name);
    });

    result.push(...sortedRows);
  } else if (displayType === 'by_day_by_location_by_item') {
    const groupedRows: Record<string, ItemReportRow> = {};

    processedData.forEach((curr) => {
      const locKey = curr.location_name || 'Unknown';
      const key = `${curr.transaction_date}||${locKey}||${curr.pos_name}||${curr.description}`;

      if (groupedRows[key]) {
        const row = groupedRows[key];
        row.total_gross_sales += curr.total_gross_sales;
        row.total_tax += curr.total_tax;
        row.total_discounts += curr.total_discounts;
        row.total_promo_discounts += curr.total_promo_discounts;
        row.total_refunds += curr.total_refunds;
        row.total_net_sales += curr.total_net_sales;
        row.cash_units_sold += curr.cash_units_sold;
        row.cash_balance_units_sold += curr.cash_balance_units_sold;
        row.credit_units_sold += curr.credit_units_sold;
        row.rfid_units_sold += curr.rfid_units_sold;
        row.token_units_sold += curr.token_units_sold;
        row.total_units_sold += curr.total_units_sold;
      } else {
        groupedRows[key] = { ...curr, location_name: locKey, id: key };
      }
    });

    const sortedRows = Object.values(groupedRows).sort((a, b) => {
      const dateA = a.transaction_hour || a.transaction_date;
      const dateB = b.transaction_hour || b.transaction_date;
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA);
      }
      const locA = a.location_name || 'Unknown';
      const locB = b.location_name || 'Unknown';
      if (locA !== locB) {
        return locA.localeCompare(locB);
      }
      return a.pos_name.localeCompare(b.pos_name);
    });

    result.push(...sortedRows);
  }

  result.push(totalRow);
  return result;
};
